import React, { useState, useEffect, useRef } from 'react';
import { formatDateTimeDDMMYYYY, formatDateDDMMYYYY, formatTimeHHmm, getMaxDateToday, getMaxDateFuture, getMaxEndDateForStart } from '../../../../utils/dateUtils';
import { formatRoles } from '../../../../utils/roleUtils';
import ConfirmModal from '../../../../components/ConfirmModal';
import CancelBookingModal from '../../../../components/CancelBookingModal';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import {
  getBookings,
  updateBooking,
  deleteBooking,
  cancelPilotAssignment,
  Booking,
  getAmbulances,
  Ambulance,
  getUsers,
  User,
  getTenants,
  getTenant,
  Tenant,
  getAttendance,
  Attendance,
  downloadBookingDetailsPDF,
  downloadBookingDetailsExcel,
} from '../../../../services/api';
import { BOOKING_TYPE_LABELS, BOOKING_TYPE_COLORS } from '../../../../constants/booking';


interface AllBookingsTabProps {
  onActionPerformed?: () => void;
  refreshTrigger?: number;
  externalStatusFilter?: string;
  useLast24Hours?: boolean;
}

const AllBookingsTab: React.FC<AllBookingsTabProps> = ({ onActionPerformed, refreshTrigger, externalStatusFilter, useLast24Hours }) => {
  const { user, hasPermission, isTenantAdmin, isSystemAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [initialLoading, setInitialLoading] = useState(true); // Only true on first load
  const [loading, setLoading] = useState(false); // For background updates, doesn't hide UI
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelBookingConfirm, setShowCancelBookingConfirm] = useState(false);
  const [showCancelPilotAssignmentConfirm, setShowCancelPilotAssignmentConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [assignMode, setAssignMode] = useState<'assign' | 'reassign'>('assign');
  const [bookingToDelete, setBookingToDelete] = useState<{ id: string; name: string } | null>(null);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [pilotAssignmentToCancel, setPilotAssignmentToCancel] = useState<string | null>(null);
  const [assigningBooking, setAssigningBooking] = useState<Booking | null>(null);
  const [listError, setListError] = useState('');
  const [assignError, setAssignError] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  // Date filter state
  type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'tomorrow' | 'next7days' | 'custom' | 'all' | 'last24hours';
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('last24hours');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [detailsDownloading, setDetailsDownloading] = useState(false);
  const [detailsDownloadError, setDetailsDownloadError] = useState<string>('');
  const [showDetailsDownloadMenu, setShowDetailsDownloadMenu] = useState(false);
  const detailsDownloadMenuRef = useRef<HTMLDivElement>(null);

  // Helper to truncate text to a fixed length with ellipsis
  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '—';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  // Close drawer with Escape key
  useEffect(() => {
    if (!showAssignModal && !showDetailsDrawer) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        if (showAssignModal) {
          setShowAssignModal(false);
        }
        if (showDetailsDrawer) {
          setShowDetailsDrawer(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAssignModal, showDetailsDrawer]);

  // Close booking-details download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsDownloadMenuRef.current &&
        !detailsDownloadMenuRef.current.contains(event.target as Node)
      ) {
        setShowDetailsDownloadMenu(false);
      }
    };

    if (showDetailsDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDetailsDownloadMenu]);

  const handleDownloadBookingDetails = async (format: 'pdf' | 'excel') => {
    if (!selectedBooking) return;

    try {
      setDetailsDownloading(true);
      setDetailsDownloadError('');
      setShowDetailsDownloadMenu(false);

      const bookingId = (selectedBooking as any)._id || (selectedBooking as any).id;
      if (!bookingId) {
        setDetailsDownloadError('Booking ID is missing for download');
        return;
      }

      let response;
      if (format === 'pdf') {
        response = await downloadBookingDetailsPDF(bookingId);
        const { url } = response.data.data;
        // Open PDF in a new tab for viewing
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      } else {
        response = await downloadBookingDetailsExcel(bookingId);
      }

      const { url, fileName } = response.data.data;
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'booking-details.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading booking details ${format.toUpperCase()}:`, err);
      setDetailsDownloadError(
        err?.response?.data?.message || `Failed to download booking details ${format.toUpperCase()}`
      );
    } finally {
      setDetailsDownloading(false);
    }
  };

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (showAssignModal || showDetailsDrawer) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showAssignModal, showDetailsDrawer]);

  // Assignment form data
  const [assignFormData, setAssignFormData] = useState({
    ambulanceId: '',
    pilotId: '',
    assignedStaffIds: [] as string[],
  });

  // Dropdown data (only for assign modal)
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [staff, setStaff] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<Attendance[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pilotSearchTerm, setPilotSearchTerm] = useState('');
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');
  const [showAmbulanceDropdown, setShowAmbulanceDropdown] = useState(false);
  const [showPilotDropdown, setShowPilotDropdown] = useState(false);
  const [debouncedStaffSearchTerm, setDebouncedStaffSearchTerm] = useState('');
  const [displayedStaffLimit, setDisplayedStaffLimit] = useState(50);

  // Fetch staff and tenants once on mount (for getBookedByName and other functions)
  useEffect(() => {
    fetchStaff();
    fetchTenants();
  }, []);

  // Sync external status filter with internal state and set 24-hour filter if needed
  useEffect(() => {
    if (externalStatusFilter !== undefined) {
      setStatusFilter(externalStatusFilter);
    } else if (externalStatusFilter === undefined && useLast24Hours) {
      // Clear status filter when Total Request is clicked
      setStatusFilter('');
    } else if (externalStatusFilter === undefined && !useLast24Hours) {
      // Reset to default (last 24 hours) when card is clicked again
      setStatusFilter('');
      setDateFilterType('last24hours');
    }
    if (useLast24Hours) {
      setDateFilterType('last24hours');
    }
  }, [externalStatusFilter, useLast24Hours]);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, dateFilterType, customStartDate, customEndDate]);

  // Refresh bookings when refreshTrigger changes (triggered by tracking API auto-refresh)
  // This should update data silently without showing loading state
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchBookingsSilently();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Debounce staff search term for API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStaffSearchTerm(staffSearchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [staffSearchTerm]);

  // Fetch staff when debounced search changes
  useEffect(() => {
    if (showAssignModal) {
      fetchStaff(debouncedStaffSearchTerm || undefined);
      setDisplayedStaffLimit(50); // Reset limit when search changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedStaffSearchTerm, showAssignModal]);

  // Refetch bookings when debounced search changes (respecting filters)
  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Silent fetch for background updates (no loading state)
  const fetchBookingsSilently = async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      setListError(''); // Clear previous errors
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const params: any = { page: pageToUse, pageSize: sizeToUse };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;

      // Helper function to format date in YYYY-MM-DD format using local timezone
      const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Helper function to format datetime in ISO format for last 24 hours
      const formatDateTimeLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      // Calculate date range based on filter type
      const getDateRange = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        switch (dateFilterType) {
          case 'today': {
            const start = new Date(today);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'yesterday': {
            const start = new Date(today);
            start.setDate(start.getDate() - 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'thisWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'lastWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek - 6;
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'thisMonth': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'lastMonth': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'tomorrow': {
            const start = new Date(today);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'next7days': {
            const start = new Date(today);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setDate(end.getDate() + 7);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'custom': {
            if (!customStartDate || !customEndDate) {
              return { startDate: undefined, endDate: undefined };
            }
            const normalizeDate = (dateInput: string): string => {
              const trimmed = dateInput.trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
              return trimmed;
            };
            const startStr = normalizeDate(customStartDate);
            const endStr = normalizeDate(customEndDate);
            const maxEndStr = getMaxEndDateForStart(startStr);
            const endDateClamped = endStr && maxEndStr && endStr > maxEndStr ? maxEndStr : endStr;
            return {
              startDate: startStr,
              endDate: endDateClamped,
            };
          }
          case 'last24hours': {
            const end = new Date(now);
            const start = new Date(now);
            start.setHours(start.getHours() - 24);
            // Send timestamps for last 24 hours to preserve exact time
            return {
              startDate: start.getTime().toString(),
              endDate: end.getTime().toString()
            };
          }
          case 'all':
            return { startDate: undefined, endDate: undefined };
          default:
            return { startDate: undefined, endDate: undefined };
        }
      };

      const dateRange = getDateRange();
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await getBookings(
        params.page,
        params.pageSize,
        params.status,
        undefined,
        undefined,
        params.priority,
        params.search,
        params.startDate,
        params.endDate
      );
      setBookings(response.data.data || []);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load bookings';
      setListError(errorMessage);
      console.error('Failed to load bookings:', err);
    }
  };

  const fetchBookings = async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      // Only show loading on initial load or user-triggered actions
      if (initialLoading) {
        setLoading(true);
      }
      setListError(''); // Clear previous errors
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const params: any = { page: pageToUse, pageSize: sizeToUse };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;

      // Helper function to format date in YYYY-MM-DD format using local timezone
      const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Helper function to format datetime in ISO format for last 24 hours
      const formatDateTimeLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      // Calculate date range based on filter type
      const getDateRange = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        switch (dateFilterType) {
          case 'today': {
            const start = new Date(today);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'yesterday': {
            const start = new Date(today);
            start.setDate(start.getDate() - 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'thisWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'lastWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek - 6;
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end)
            };
          }
          case 'thisMonth': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'lastMonth': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'tomorrow': {
            const start = new Date(today);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'next7days': {
            const start = new Date(today);
            start.setDate(start.getDate() + 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setDate(end.getDate() + 7);
            end.setHours(23, 59, 59, 999);
            return {
              startDate: formatDateLocal(start),
              endDate: formatDateLocal(end),
            };
          }
          case 'custom': {
            if (!customStartDate || !customEndDate) {
              return { startDate: undefined, endDate: undefined };
            }
            const normalizeDate = (dateInput: string): string => {
              const trimmed = dateInput.trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
              return trimmed;
            };
            const startStr = normalizeDate(customStartDate);
            const endStr = normalizeDate(customEndDate);
            const maxEndStr = getMaxEndDateForStart(startStr);
            const endDateClamped = endStr && maxEndStr && endStr > maxEndStr ? maxEndStr : endStr;
            return {
              startDate: startStr,
              endDate: endDateClamped,
            };
          }
          case 'last24hours': {
            const end = new Date(now);
            const start = new Date(now);
            start.setHours(start.getHours() - 24);
            // Send timestamps for last 24 hours to preserve exact time
            return {
              startDate: start.getTime().toString(),
              endDate: end.getTime().toString()
            };
          }
          case 'all':
            return { startDate: undefined, endDate: undefined };
          default:
            return { startDate: undefined, endDate: undefined };
        }
      };

      const dateRange = getDateRange();
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await getBookings(
        params.page,
        params.pageSize,
        params.status,
        undefined,
        undefined,
        params.priority,
        params.search,
        params.startDate,
        params.endDate
      );
      setBookings(response.data.data || []);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
      // Only set initialLoading to false on first successful load
      if (initialLoading) {
        setInitialLoading(false);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load bookings';
      setListError(errorMessage);
      console.error('Failed to load bookings:', err);
      // Only set initialLoading to false on first load (even if error)
      if (initialLoading) {
        setInitialLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchBookings();
  };

  const handlePageChange = (page: number) => {
    fetchBookings(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    fetchBookings(1, size);
  };

  const fetchAmbulances = async () => {
    try {
      const response = await getAmbulances(1, 1000);
      setAmbulances(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load ambulances:', err);
    }
  };

  const fetchStaff = async (search?: string) => {
    try {
      // Fetch more users if searching, otherwise limit to reasonable number
      const pageSize = search ? 500 : 200;
      const response = await getUsers(1, pageSize, search);
      setStaff(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load staff:', err);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await getTenants(1, 1000);
      setTenants(response.data.data || []);
    } catch (err: any) {
      console.warn('Failed to load tenants list, trying own tenant:', err?.response?.status || err);
      if (user?.tenantId) {
        try {
          const own = await getTenant(user.tenantId);
          if (own.data?.data) {
            setTenants([own.data.data]);
            return;
          }
        } catch (ownErr) {
          console.error('Failed to load own tenant:', ownErr);
        }
      }
      setTenants([]);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      // Fetch latest checked-in records (no date constraint)
      const response = await getAttendance(1, 1000, undefined, undefined, undefined, 'checkedIn');
      setTodayAttendance(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load today\'s attendance:', err);
      setTodayAttendance([]);
    }
  };

  const getBookedByName = (bookedBy: string, bookedByDisplay?: string): string => {
    if (bookedByDisplay) {
      return bookedByDisplay;
    }
    if (!bookedBy) {
      return '—';
    }
    // First, try to find in staff/users
    const staffMember = staff.find((s) => (s._id || s.id) === bookedBy);
    if (staffMember) {
      const result = staffMember.metadata?.name || staffMember.name || bookedBy;
      return result;
    }

    // If not found, try to find in tenants (for tenant admin bookings)
    const tenant = tenants.find((t) => t._id === bookedBy);
    if (tenant) {
      const result = tenant.metadata?.name || bookedBy;
      return result;
    }

    return bookedBy;
  };


  const handleOpenAssignModal = (booking: Booking, mode: 'assign' | 'reassign' = 'assign') => {
    setAssignMode(mode);
    setAssigningBooking(booking);
    setAssignFormData({
      ambulanceId: booking.spec.ambulanceId || '',
      pilotId: booking.spec.pilotId || '',
      assignedStaffIds: booking.spec.assignedStaffIds || [],
    });
    setPilotSearchTerm('');
    setStaffSearchTerm('');
    setDebouncedStaffSearchTerm('');
    setDisplayedStaffLimit(50);
    setShowAssignModal(true);
    fetchTodayAttendance();
    setAssignError('');
    // Fetch staff and ambulances when modal opens (for dropdowns)
    fetchStaff();
    fetchAmbulances();
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssigningBooking(null);
    setAssignMode('assign');
    setAmbulanceSearchTerm('');
    setShowAmbulanceDropdown(false);
    setPilotSearchTerm('');
    setShowPilotDropdown(false);
    setStaffSearchTerm('');
    setDebouncedStaffSearchTerm('');
    setDisplayedStaffLimit(50);
    setAssignError('');
  };

  // Refs for dropdowns
  const ambulanceDropdownRef = useRef<HTMLDivElement>(null);
  const pilotDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ambulanceDropdownRef.current && !ambulanceDropdownRef.current.contains(event.target as Node)) {
        setShowAmbulanceDropdown(false);
      }
      if (pilotDropdownRef.current && !pilotDropdownRef.current.contains(event.target as Node)) {
        setShowPilotDropdown(false);
      }
    };

    if (showAmbulanceDropdown || showPilotDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAmbulanceDropdown, showPilotDropdown]);

  const handleAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (assignSubmitting) return;
    setAssignError('');
    setAssignSubmitting(true);

    if (!assigningBooking) {
      setAssignSubmitting(false);
      return;
    }

    // Validate that ambulance and pilot are selected (staff is optional)
    const hasAmbulance = !!assignFormData.ambulanceId;
    const hasPilot = !!assignFormData.pilotId;

    if (!hasAmbulance || !hasPilot) {
      const missingFields = [];
      if (!hasAmbulance) missingFields.push('Ambulance');
      if (!hasPilot) missingFields.push('Pilot/Driver');
      setAssignError(`Please select ${missingFields.join(', ')}`);
      setAssignSubmitting(false);
      return;
    }

    try {
      // Backend will handle:
      // 1. Checking if pilot/staff is locked (in_transit/arrived) - will return error if locked
      // 2. Automatically removing pilot/staff from other pending/assigned bookings

      // Determine if we should update status to 'assigned'
      // Status should change to 'assigned' if:
      // - Current status is 'pending'
      // - Ambulance and pilot are assigned (staff is optional)
      const shouldUpdateToAssigned =
        assigningBooking.spec.status === 'pending' &&
        hasAmbulance &&
        hasPilot;

      const data: any = {
        spec: {
          ambulanceId: assignFormData.ambulanceId || undefined,
          pilotId: assignFormData.pilotId || undefined,
          assignedStaffIds: assignFormData.assignedStaffIds,
          ...(shouldUpdateToAssigned && { status: 'assigned' }),
        },
      };

      await updateBooking(assigningBooking._id, data);

      handleCloseAssignModal();
      fetchBookings();
      // Trigger refresh of live tracking
      onActionPerformed?.();
    } catch (err: any) {
      setAssignError(err.response?.data?.message || 'Operation failed');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleStatusUpdate = async (bookingId: string, newStatus: string) => {
    // Show confirmation for cancellation
    if (newStatus === 'cancelled') {
      setBookingToCancel(bookingId);
      setShowCancelBookingConfirm(true);
      return;
    }

    try {

      await updateBooking(bookingId, { spec: { status: newStatus } });

      fetchBookings();
      // Trigger refresh of live tracking
      onActionPerformed?.();
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const confirmCancelBooking = async (cancellationReason: string) => {
    if (!bookingToCancel) return;

    try {
      await updateBooking(bookingToCancel, {
        spec: { status: 'cancelled', cancellationReason: cancellationReason || undefined },
      });
      setShowCancelBookingConfirm(false);
      setBookingToCancel(null);
      fetchBookings();
      onActionPerformed?.();
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to cancel booking');
      throw err;
    }
  };

  const handleDelete = (booking: Booking) => {
    setBookingToDelete({ id: booking._id, name: booking.metadata.name });
    setShowDeleteConfirm(true);
  };

  const handleCancelPilotAssignment = (bookingId: string) => {
    setPilotAssignmentToCancel(bookingId);
    setShowCancelPilotAssignmentConfirm(true);
  };

  const confirmCancelPilotAssignment = async (cancellationReason: string) => {
    if (!pilotAssignmentToCancel) return;

    try {
      await cancelPilotAssignment(pilotAssignmentToCancel, cancellationReason);
      setShowCancelPilotAssignmentConfirm(false);
      setPilotAssignmentToCancel(null);
      fetchBookings();
      // Trigger refresh of live tracking
      onActionPerformed?.();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to cancel pilot assignment';
      setListError(errorMessage);
      setShowCancelPilotAssignmentConfirm(false);
      setPilotAssignmentToCancel(null);
    }
  };

  const confirmDelete = async () => {
    if (!bookingToDelete) return;

    try {
      await deleteBooking(bookingToDelete.id);
      setShowDeleteConfirm(false);
      setBookingToDelete(null);
      fetchBookings();
      // Trigger refresh of live tracking
      onActionPerformed?.();
    } catch (err: any) {
      setListError(err.response?.data?.message || 'Failed to delete booking');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#ff9800';
      case 'assigned':
        return '#2196f3';
      case 'in_transit':
        return '#9c27b0';
      case 'patient_picked':
        return '#00bcd4';
      case 'patient_dropped':
        return '#795548';
      case 'completed':
        return '#4caf50';
      case 'cancelled':
        return '#fa003f';
      default:
        return '#999';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  const getLastStatusUpdateTime = (booking: Booking): number | null => {
    const timings = booking.spec.timings;
    if (!timings) return null;

    const patientPickedTime = (timings as { patientPickedTime?: number }).patientPickedTime ?? timings.arrivedTime;
    const statusUpdateTimes = [
      timings.pendingTime,
      timings.assignedTime,
      timings.inTransitTime,
      patientPickedTime,
      (timings as { patientDroppedTime?: number }).patientDroppedTime,
      timings.cancelledTime,
      timings.completionTime,
    ].filter((time): time is number => typeof time === 'number' && time > 0);

    return statusUpdateTimes.length > 0 ? Math.max(...statusUpdateTimes) : null;
  };

  // Helper function to truncate address to short form
  const truncateAddress = (address: string, maxLength: number = 40): string => {
    if (!address) return '';
    if (address.length <= maxLength) return address;
    return address.substring(0, maxLength) + '...';
  };

  // Helper function to format location for display
  const formatLocation = (location: { latitude: number; longitude: number; address?: string } | null | undefined): { short: string; full: string | null; hasAddress: boolean } => {
    if (!location) {
      return { short: '—', full: null, hasAddress: false };
    }

    const coords = `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;

    // If there's an address, use it; otherwise use coordinates
    if (location.address) {
      return {
        short: truncateAddress(location.address, 40),
        full: location.address,
        hasAddress: true,
      };
    } else {
      return {
        short: coords,
        full: null,
        hasAddress: false,
      };
    }
  };

  return (
    <div>

      {listError && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px' }}>
          {listError}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Bookings</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end', }}>
            <input
              type="text"
              placeholder="Search by reference, patient, ambulance, pilot, or booked by..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
                border: '1px solid',
                borderColor: isDark ? '#334155' : '#ddd',
                height: '36px',
                boxSizing: 'border-box',
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '8px', border: '1px solid', borderColor: isDark ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '105px' }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="in_transit">In Transit</option>
              <option value="patient_picked">Patient Picked</option>
              <option value="patient_dropped">Patient Dropped</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              style={{ padding: '8px', border: '1px solid', borderColor: isDark ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '125px' }}
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <select
              value={dateFilterType}
              onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
              style={{ padding: '8px', border: '1px solid', borderColor: isDark ? '#334155' : '#ddd', borderRadius: '4px', height: '36px', boxSizing: 'border-box', width: '150px' }}
            >
              <option value="all">All Dates</option>
              <option value="last24hours">Last 24 Hours</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="thisWeek">This Week</option>
              <option value="lastWeek">Last Week</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="next7days">Next 7 Days</option>
              <option value="custom">Custom Dates</option>
            </select>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
        {dateFilterType === 'custom' && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '10px', padding: '0 20px 10px 20px' }}>
            <label style={{
              fontSize: '16px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              color: isDark ? '#f1f5f9' : '#1e293b'
            }}>Start Date:</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => {
                const value = e.target.value;
                setCustomStartDate(value);
                if (value && customEndDate) {
                  const start = new Date(value);
                  const end = new Date(customEndDate);
                  const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                  if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                }
              }}
              max={getMaxDateFuture()}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '16px',
                border: isDark ? '1px solid #334155' : '1px solid #ddd',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#1e293b',
                height: '36px',
                boxSizing: 'border-box',
                width: '180px',
              }}
            />
            <label style={{
              fontSize: '16px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              color: isDark ? '#f1f5f9' : '#1e293b'
            }}>End Date:</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              min={customStartDate || undefined}
              max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateFuture()}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '16px',
                border: isDark ? '1px solid #334155' : '1px solid #ddd',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#1e293b',
                height: '36px',
                boxSizing: 'border-box',
                width: '180px',
              }}
            />
          </div>
        )}

        {initialLoading ? (
          <div className="loading">Loading bookings...</div>
        ) : bookings.length === 0 ? (
          <div className="empty-state" style={{ height: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            <h3>No bookings yet</h3>
            {/* <p>Create a new booking to get started</p> */}
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 320px)', overflowY: 'auto' }}>
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Reference</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Priority</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Booking Type</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Pilot</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Patient</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Contact Person</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Ambulance</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Booked By</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Pickup Location</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Dropoff Location</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Booking Time</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Last Update</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => {
                    // Use populated data from booking API instead of separate lookups
                    const patientName = booking.spec.patientInfo?.name || booking.spec.patientId || '—';
                    const patientNumber = booking.spec.patientInfo?.number || '—';
                    const contactPersonName = booking.spec.patientInfo?.contactPersonName || '—';
                    const contactPersonNumber = booking.spec.patientInfo?.contactPersonNumber || '—';
                    const ambulanceName = booking.spec.ambulanceInfo?.name || '—';
                    const pilotName = booking.spec.pilotInfo?.name || '—';
                    const pilotNumber = booking.spec.pilotInfo?.number || '—';

                    return (
                      <tr
                        key={booking._id}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          // Avoid opening drawer when clicking action buttons or their children
                          if (target && typeof target.closest === 'function' && target.closest('.action-buttons')) {
                            return;
                          }
                          setSelectedBooking(booking);
                          setShowDetailsDrawer(true);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td style={{ whiteSpace: 'nowrap' }}>{(booking.metadata?.name ?? '').toUpperCase()}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: 'white',
                              background: getPriorityColor(booking.spec.priority),
                            }}
                          >
                            {booking.spec.priority.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: 'white',
                              background: booking.spec.bookingType ? BOOKING_TYPE_COLORS[booking.spec.bookingType] : '#e2e8f0',
                            }}
                          >
                            {booking.spec.bookingType ? BOOKING_TYPE_LABELS[booking.spec.bookingType] : '—'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: 'white',
                              background: getStatusColor(booking.spec.status),
                            }}
                          >
                            {booking.spec.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div
                            className="action-buttons"
                            style={{
                              display: 'flex',
                              flexWrap: 'nowrap',
                              gap: '6px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {hasPermission('booking.update') && booking.spec.status === 'pending' && (
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleOpenAssignModal(booking, 'assign')}
                                title="Assign ambulance and staff"
                              >
                                Assign
                              </button>
                            )}
                            {hasPermission('booking.update') && booking.spec.status === 'assigned' && (
                              <>
                                <button
                                  className="btn-icon"
                                  onClick={() => handleOpenAssignModal(booking, 'reassign')}
                                  title="Reassign ambulance and staff"
                                  style={{ background: '#f97316', color: 'white' }}
                                >
                                  Reassign
                                </button>
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStatusUpdate(booking._id, 'in_transit')}
                                  title="Start trip"
                                  style={{ background: '#9c27b0', color: 'white' }}
                                >
                                  Start Trip
                                </button>
                              </>
                            )}
                            {hasPermission('booking.update') && booking.spec.status === 'in_transit' && (
                              <button
                                className="btn-icon"
                                onClick={() => handleStatusUpdate(booking._id, 'patient_picked')}
                                title="Mark patient picked up"
                                style={{ background: '#00bcd4', color: 'white' }}
                              >
                                Patient Pickedup
                              </button>
                            )}
                            {hasPermission('booking.update') && booking.spec.status === 'patient_picked' && booking.spec.bookingType === 'from_hospital' && (
                              <button
                                className="btn-icon"
                                onClick={() => handleStatusUpdate(booking._id, 'patient_dropped')}
                                title="Mark patient dropped"
                                style={{ background: '#795548', color: 'white' }}
                              >
                                Patient Dropped
                              </button>
                            )}
                            {hasPermission('booking.update') && ((booking.spec.status === 'patient_picked' && booking.spec.bookingType === 'to_hospital') || booking.spec.status === 'patient_dropped') && (
                              <button
                                className="btn-icon"
                                onClick={() => handleStatusUpdate(booking._id, 'completed')}
                                title="Complete booking"
                                style={{ background: '#4caf50', color: 'white' }}
                              >
                                Complete
                              </button>
                            )}
                            {/* Pilot can cancel their assignment if they are assigned and status is pending/assigned */}
                            {booking.spec.pilotId === (user?._id || user?.id) &&
                              (booking.spec.status === 'pending' || booking.spec.status === 'assigned') && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleCancelPilotAssignment(booking._id)}
                                  title="Cancel my assignment"
                                  style={{ background: '#ff9800', color: 'white' }}
                                >
                                  Cancel Assignment
                                </button>
                              )}
                            {/* Only tenant admin can cancel the whole booking */}
                            {hasPermission('booking.update') &&
                              (isTenantAdmin || isSystemAdmin) &&
                              (booking.spec.status === 'pending' || booking.spec.status === 'assigned') && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
                                  title="Cancel booking"
                                  style={{ background: '#fa003f', color: 'white' }}
                                >
                                  Cancel
                                </button>
                              )}
                            {hasPermission('booking.delete') && booking.spec.status === 'cancelled' && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDelete(booking)}
                                title="Delete booking"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          {pilotName !== '—' || pilotNumber !== '—' ? (
                            <div>
                              <div>{pilotName}</div>
                              {pilotNumber !== '—' && <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>{pilotNumber}</div>}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {patientName !== '—' || patientNumber !== '—' ? (
                            <div>
                              <div>{patientName}</div>
                              {patientNumber !== '—' && <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>{patientNumber}</div>}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {contactPersonName !== '—' || contactPersonNumber !== '—' ? (
                            <div>
                              {contactPersonName !== '—' && <div>{contactPersonName}</div>}
                              {contactPersonNumber !== '—' && <div style={{ fontSize: '14px', color: '#666', marginTop: contactPersonName !== '—' ? '2px' : '0' }}>{contactPersonNumber}</div>}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{truncateText(ambulanceName, 12)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{getBookedByName(booking.spec.bookedBy, booking.spec.bookedByDisplay)}</td>
                        <td
                          style={{
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {(() => {
                            const pickupLoc = formatLocation(booking.spec.pickupLocation);
                            return pickupLoc.hasAddress ? (
                              <span
                                title={pickupLoc.full || ''}
                                style={{
                                  cursor: 'help',
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {pickupLoc.short}
                              </span>
                            ) : (
                              <span>{pickupLoc.short}</span>
                            );
                          })()}
                        </td>
                        <td
                          style={{
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {(() => {
                            const dropoffLoc = formatLocation(booking.spec.dropoffLocation);
                            return dropoffLoc.hasAddress ? (
                              <span
                                title={dropoffLoc.full || ''}
                                style={{
                                  cursor: 'help',
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {dropoffLoc.short}
                              </span>
                            ) : (
                              <span>{dropoffLoc.short}</span>
                            );
                          })()}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {booking.spec.timings?.bookingTime
                            ? formatDateTimeDDMMYYYY(booking.spec.timings.bookingTime)
                            : booking.metadata.createdAt
                              ? formatDateTimeDDMMYYYY(booking.metadata.createdAt)
                              : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            const lastStatusUpdate = getLastStatusUpdateTime(booking);
                            return lastStatusUpdate ? formatDateTimeDDMMYYYY(lastStatusUpdate) : (booking.metadata.updatedAt ? formatDateTimeDDMMYYYY(booking.metadata.updatedAt) : '—');
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 15, 20, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      totalItems
                    )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assign Ambulance/Staff Drawer */}
      {showAssignModal && assigningBooking && (
        <div className="drawer-overlay" onClick={handleCloseAssignModal}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {assignMode === 'reassign' ? 'Reassign Ambulance & Staff' : 'Assign Ambulance & Staff'}
              </h3>
              <button className="drawer-close" onClick={handleCloseAssignModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleAssignSubmit} className="drawer-form">
              <div className="drawer-body" style={{ position: 'relative' }}>
                {assignError && <div className="error-message">{assignError}</div>}

                {(showAmbulanceDropdown || showPilotDropdown) && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      cursor: 'not-allowed',
                      backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showAmbulanceDropdown) setShowAmbulanceDropdown(false);
                      if (showPilotDropdown) setShowPilotDropdown(false);
                    }}
                  />
                )}

                <div className="form-group">
                  <label>Ambulance</label>
                  <div ref={ambulanceDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showAmbulanceDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAmbulanceDropdown(!showAmbulanceDropdown);
                        if (!showAmbulanceDropdown) {
                          setAmbulanceSearchTerm('');
                        }
                      }}
                      className="custom-select-button"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedAmbulance = ambulances.find((a) => a._id === assignFormData.ambulanceId);
                          return selectedAmbulance
                            ? `${selectedAmbulance.metadata.name} (${selectedAmbulance.spec.licensePlate})`
                            : 'Select an ambulance';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showAmbulanceDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showAmbulanceDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search ambulance by name, license plate, or type..."
                          value={ambulanceSearchTerm}
                          onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            const searchLower = ambulanceSearchTerm.toLowerCase().trim();

                            const filteredAmbulances = ambulances.filter((a) => {
                              // Exclude ambulances that are on duty
                              if (a.spec.status === 'on_duty') return false;

                              // Filter by search term
                              if (searchLower !== '') {
                                const name = (a.metadata.name || '').toLowerCase();
                                const licensePlate = (a.spec.licensePlate || '').toLowerCase();
                                const type = (a.spec.type || '').toLowerCase();
                                if (!name.includes(searchLower) && !licensePlate.includes(searchLower) && !type.includes(searchLower)) {
                                  return false;
                                }
                              }

                              return true;
                            });

                            if (filteredAmbulances.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No ambulances found
                                </div>
                              );
                            }

                            return filteredAmbulances.map((ambulance) => {
                              const isAvailable = ambulance.spec.status === 'available';
                              const statusClass = isAvailable ? 'ambulance-status-available' : 'ambulance-status-other';

                              const handleSelect = () => {
                                const val = ambulance._id;
                                const selectedAmbulance = ambulances.find((a) => a._id === val);

                                // Get filtering data for validation
                                const activeStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                                const lockedStatuses = ['in_transit', 'patient_picked', 'patient_dropped'];

                                // Get ambulances that are on duty
                                const ambulancesOnDuty = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.ambulanceId &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.ambulanceId)
                                );

                                // Get pilots assigned to ambulances that are on duty
                                const pilotsOnDutyAmbulances = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.ambulanceId &&
                                      ambulancesOnDuty.has(b.spec.ambulanceId) &&
                                      b.spec.pilotId &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.pilotId)
                                );

                                // Get staff assigned to ambulances that are on duty
                                const staffOnDutyAmbulances = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.ambulanceId &&
                                      ambulancesOnDuty.has(b.spec.ambulanceId) &&
                                      b.spec.assignedStaffIds &&
                                      b.spec.assignedStaffIds.length > 0 &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .flatMap((b) => b.spec.assignedStaffIds || [])
                                );

                                const assignedPilotIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.pilotId &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.pilotId)
                                );

                                const lockedPilotIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.pilotId &&
                                      lockedStatuses.includes(b.spec.status)
                                    )
                                    .map((b) => b.spec.pilotId)
                                );

                                const assignedStaffIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.assignedStaffIds &&
                                      b.spec.assignedStaffIds.length > 0 &&
                                      activeStatuses.includes(b.spec.status)
                                    )
                                    .flatMap((b) => b.spec.assignedStaffIds || [])
                                );

                                const lockedStaffIds = new Set(
                                  bookings
                                    .filter((b) =>
                                      b._id !== assigningBooking?._id &&
                                      b.spec.assignedStaffIds &&
                                      b.spec.assignedStaffIds.length > 0 &&
                                      lockedStatuses.includes(b.spec.status)
                                    )
                                    .flatMap((b) => b.spec.assignedStaffIds || [])
                                );

                                // Get set of user IDs who are checked in today
                                const presentTodayUserIds = new Set(
                                  todayAttendance
                                    .filter(a => a.spec.status === 'checkedIn')
                                    .map(a => a.spec.userId)
                                );

                                setAssignFormData((prev) => {
                                  const updates: any = { ambulanceId: val };

                                  // Auto-populate pilot if ambulance has one assigned AND pilot meets criteria
                                  if (selectedAmbulance?.spec?.assignedPilot?.userId) {
                                    const pilotId = selectedAmbulance.spec.assignedPilot.userId;

                                    // Check if pilot meets all criteria
                                    const isPresentToday = presentTodayUserIds.has(pilotId);
                                    const isNotAssigned = !assignedPilotIds.has(pilotId);
                                    const isNotOnDutyAmbulance = !pilotsOnDutyAmbulances.has(pilotId);
                                    const isNotLocked = !lockedPilotIds.has(pilotId);
                                    const isCurrentBooking = pilotId === assigningBooking?.spec.pilotId;

                                    if (isPresentToday && (isNotAssigned || isCurrentBooking) && isNotOnDutyAmbulance && isNotLocked) {
                                      updates.pilotId = pilotId;
                                    }
                                  }

                                  // Auto-populate staff if ambulance has assigned staff AND they meet criteria
                                  if (selectedAmbulance?.spec?.assignedStaffs && selectedAmbulance.spec.assignedStaffs.length > 0) {
                                    const staffToAdd: string[] = [];

                                    selectedAmbulance.spec.assignedStaffs.forEach((assignedStaff) => {
                                      const staffId = assignedStaff.userId;

                                      // Find the staff member to check their details
                                      const staffMember = staff.find((s) => (s._id || s.id) === staffId);
                                      if (staffMember) {
                                        // Check if staff is not a pilot/driver
                                        const staffCategory = (staffMember.spec?.staffCategory || '').toLowerCase();
                                        const roles = (staffMember.spec?.roles || []).map((r: string) => r.toLowerCase());
                                        const isPilot = staffCategory === 'pilot' || staffCategory === 'driver' || roles.some((r) => r.includes('pilot') || r.includes('driver'));

                                        if (!isPilot) {
                                          // Check if staff meets all criteria
                                          const isPresentToday = presentTodayUserIds.has(staffId);
                                          const isNotAssigned = !assignedStaffIds.has(staffId);
                                          const isNotOnDutyAmbulance = !staffOnDutyAmbulances.has(staffId);
                                          const isNotLocked = !lockedStaffIds.has(staffId);
                                          const isCurrentBooking = assigningBooking?.spec.assignedStaffIds?.includes(staffId);

                                          if (isPresentToday && (isNotAssigned || isCurrentBooking) && isNotOnDutyAmbulance && isNotLocked) {
                                            // Add staff to list if not already present
                                            if (!prev.assignedStaffIds.includes(staffId) && !staffToAdd.includes(staffId)) {
                                              staffToAdd.push(staffId);
                                            }
                                          }
                                        }
                                      }
                                    });

                                    if (staffToAdd.length > 0) {
                                      updates.assignedStaffIds = [...prev.assignedStaffIds, ...staffToAdd];
                                    }
                                  }

                                  return { ...prev, ...updates };
                                });
                                setShowAmbulanceDropdown(false);
                                setAmbulanceSearchTerm('');
                              };

                              return (
                                <div
                                  key={ambulance._id}
                                  onClick={handleSelect}
                                  className={`custom-dropdown-option ${statusClass} ${assignFormData.ambulanceId === ambulance._id ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                  }}
                                >
                                  <div style={{ fontWeight: assignFormData.ambulanceId === ambulance._id ? '600' : 'normal' }}>
                                    {ambulance.metadata.name} ({ambulance.spec.licensePlate})
                                  </div>
                                  {ambulance.spec.type && (
                                    <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                                      Type: {ambulance.spec.type}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Pilot/Driver</label>
                  <div ref={pilotDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showPilotDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPilotDropdown(!showPilotDropdown);
                        if (!showPilotDropdown) {
                          setPilotSearchTerm('');
                        }
                      }}
                      className="custom-select-button"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedPilot = staff.find((s) => (s._id || s.id) === assignFormData.pilotId);
                          return selectedPilot
                            ? selectedPilot.metadata?.name || selectedPilot.name
                            : 'Select a pilot';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showPilotDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showPilotDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search pilot by name, phone, or role..."
                          value={pilotSearchTerm}
                          onChange={(e) => setPilotSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            // Get list of pilots assigned to active bookings (excluding current booking)
                            // Lock pilots assigned to in_transit or arrived bookings
                            const activeStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                            const lockedStatuses = ['in_transit', 'patient_picked', 'patient_dropped'];

                            // Get ambulances that are on duty (have active bookings)
                            const ambulancesOnDuty = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.ambulanceId &&
                                  activeStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.ambulanceId)
                            );

                            // Get pilots assigned to ambulances that are on duty
                            const pilotsOnDutyAmbulances = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.ambulanceId &&
                                  ambulancesOnDuty.has(b.spec.ambulanceId) &&
                                  b.spec.pilotId &&
                                  activeStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.pilotId)
                            );

                            const assignedPilotIds = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.pilotId &&
                                  activeStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.pilotId)
                            );
                            // Lock pilots from in_transit/arrived bookings
                            const lockedPilotIds = new Set(
                              bookings
                                .filter((b) =>
                                  b._id !== assigningBooking?._id &&
                                  b.spec.pilotId &&
                                  lockedStatuses.includes(b.spec.status)
                                )
                                .map((b) => b.spec.pilotId)
                            );

                            const searchLower = pilotSearchTerm.toLowerCase().trim();

                            // Get set of user IDs who are checked in today
                            const presentTodayUserIds = new Set(
                              todayAttendance
                                .filter(a => a.spec.status === 'checkedIn')
                                .map(a => a.spec.userId)
                            );

                            const filteredPilots = staff.filter((s) => {
                              const pilotId = s._id || s.id || '';

                              // Only show pilots who are present today
                              if (!presentTodayUserIds.has(pilotId)) {
                                return false;
                              }

                              // Only show pilots/drivers in pilot dropdown (case-insensitive)
                              // Exclude paramedics explicitly
                              const staffCategory = (s.spec?.staffCategory || '').toLowerCase();
                              const roles = (s.spec?.roles || []).map((r: string) => r.toLowerCase());

                              // Exclude if it's a paramedic
                              if (staffCategory === 'paramedic' || roles.some((r) => r.includes('paramedic'))) {
                                return false;
                              }

                              // Only include if category is pilot or has pilot role
                              const isPilot =
                                staffCategory === 'pilot' || roles.some((r) => r === 'pilot' || r.includes('ambulance pilot') || r.includes('driver'));
                              if (!isPilot) return false;

                              // Filter by search term (name, phone, role)
                              if (searchLower !== '') {
                                const name = (s.metadata?.name || s.name || '').toLowerCase();
                                const phone = (s.spec?.phone || s.phone || '').toLowerCase();
                                const roleStr = (s.spec?.roles || s.roles || []).join(' ').toLowerCase();

                                if (!name.includes(searchLower) &&
                                  !phone.includes(searchLower) &&
                                  !roleStr.includes(searchLower)) {
                                  return false;
                                }
                              }

                              // Show if not assigned to another active booking OR if already assigned to this booking
                              // But exclude if locked (in_transit or arrived) or assigned to an ambulance on duty
                              const pilotStaffId = s._id || s.id || '';
                              const isLocked = lockedPilotIds.has(pilotStaffId);
                              if (isLocked) return false; // Don't show locked pilots
                              const isOnDutyAmbulance = pilotsOnDutyAmbulances.has(pilotStaffId);
                              if (isOnDutyAmbulance) return false; // Don't show pilots assigned to ambulances on duty
                              const isNotAssigned = !assignedPilotIds.has(pilotStaffId);
                              const isCurrentBooking = pilotStaffId === assigningBooking?.spec.pilotId;
                              return isNotAssigned || isCurrentBooking;
                            });

                            if (filteredPilots.length === 0) {
                              return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No pilots found
                                </div>
                              );
                            }

                            return filteredPilots.map((pilot) => {
                              const handleSelect = () => {
                                const val = pilot._id || pilot.id || '';
                                setAssignFormData({ ...assignFormData, pilotId: val });
                                setShowPilotDropdown(false);
                                setPilotSearchTerm('');
                              };

                              return (
                                <div
                                  key={pilot._id || pilot.id}
                                  onClick={handleSelect}
                                  className={`custom-dropdown-option ${assignFormData.pilotId === (pilot._id || pilot.id) ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    const isSelected = assignFormData.pilotId === (pilot._id || pilot.id);
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const isSelected = assignFormData.pilotId === (pilot._id || pilot.id);
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                >
                                  <div style={{ fontWeight: assignFormData.pilotId === (pilot._id || pilot.id) ? '600' : 'normal' }}>
                                    {(() => {
                                      const name = pilot.metadata?.name || pilot.name || '';
                                      const roles = (pilot.spec?.roles || pilot.roles || []);
                                      const roleStr = roles.length > 0 ? formatRoles(roles) : '';
                                      return roleStr ? `${name} (${roleStr})` : name;
                                    })()}
                                  </div>
                                  {pilot.spec?.phone && (
                                    <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                                      {pilot.spec.phone}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Assign Staff (optional)</label>
                  <div
                    className="permission-list"
                    style={{
                      maxHeight: 'min(600px, 40vh)',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {(() => {
                      // Get list of staff assigned to active bookings (excluding current booking)
                      // Lock staff assigned to in_transit or arrived bookings
                      const activeStatuses = ['pending', 'assigned', 'in_transit', 'patient_picked', 'patient_dropped'];
                      const lockedStatuses = ['in_transit', 'patient_picked', 'patient_dropped'];

                      // Get ambulances that are on duty (have active bookings)
                      const ambulancesOnDuty = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.ambulanceId &&
                            activeStatuses.includes(b.spec.status)
                          )
                          .map((b) => b.spec.ambulanceId)
                      );

                      // Get staff assigned to ambulances that are on duty
                      const staffOnDutyAmbulances = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.ambulanceId &&
                            ambulancesOnDuty.has(b.spec.ambulanceId) &&
                            b.spec.assignedStaffIds &&
                            b.spec.assignedStaffIds.length > 0 &&
                            activeStatuses.includes(b.spec.status)
                          )
                          .flatMap((b) => b.spec.assignedStaffIds || [])
                      );

                      const assignedStaffIds = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.assignedStaffIds &&
                            b.spec.assignedStaffIds.length > 0 &&
                            activeStatuses.includes(b.spec.status)
                          )
                          .flatMap((b) => b.spec.assignedStaffIds || [])
                      );
                      // Lock staff from in_transit/arrived bookings
                      const lockedStaffIds = new Set(
                        bookings
                          .filter((b) =>
                            b._id !== assigningBooking?._id &&
                            b.spec.assignedStaffIds &&
                            b.spec.assignedStaffIds.length > 0 &&
                            lockedStatuses.includes(b.spec.status)
                          )
                          .flatMap((b) => b.spec.assignedStaffIds || [])
                      );

                      const searchLower = debouncedStaffSearchTerm.toLowerCase().trim();

                      // Get set of user IDs who are checked in today
                      const presentTodayUserIds = new Set(
                        todayAttendance
                          .filter(a => a.spec.status === 'checkedIn')
                          .map(a => a.spec.userId)
                      );

                      const filteredStaff = staff.filter((s) => {
                        const staffMemberId = s._id || s.id || '';

                        // Only show staff who are present today
                        if (!presentTodayUserIds.has(staffMemberId)) {
                          return false;
                        }

                        // Exclude only the pilot selected above (so they don't appear in both Pilot and Assign Staff)
                        const selectedPilotId = assignFormData.pilotId?.trim() || '';
                        if (selectedPilotId && staffMemberId === selectedPilotId) {
                          return false;
                        }

                        // Filter by search term (name, phone, role, category)
                        if (searchLower !== '') {
                          const name = (s.metadata?.name || s.name || '').toLowerCase();
                          const phone = (s.spec?.phone || s.phone || '').toLowerCase();
                          const roles = (s.spec?.roles || s.roles || []).map((r: string) => r.toLowerCase());
                          const roleStr = roles.join(' ').toLowerCase();
                          const category = (s.spec?.staffCategory || '').toLowerCase();

                          if (!name.includes(searchLower) &&
                            !phone.includes(searchLower) &&
                            !roleStr.includes(searchLower) &&
                            !category.includes(searchLower)) {
                            return false;
                          }
                        }

                        // Show if not assigned to another active booking OR if already assigned to this booking
                        // But exclude if locked (in_transit or arrived) or assigned to an ambulance on duty
                        const staffId = s._id || s.id || '';
                        const isLocked = lockedStaffIds.has(staffId);
                        if (isLocked) return false; // Don't show locked staff
                        const isOnDutyAmbulance = staffOnDutyAmbulances.has(staffId);
                        if (isOnDutyAmbulance) return false; // Don't show staff assigned to ambulances on duty
                        const isNotAssigned = !assignedStaffIds.has(staffId);
                        const isCurrentBooking = assigningBooking?.spec.assignedStaffIds?.includes(staffId);

                        return isNotAssigned || isCurrentBooking;
                      });

                      const displayedStaff = filteredStaff.slice(0, displayedStaffLimit);
                      const hasMore = filteredStaff.length > displayedStaffLimit;

                      return (
                        <>
                          <input
                            type="text"
                            placeholder="Search staff by name, phone, role, or category..."
                            value={staffSearchTerm}
                            onChange={(e) => setStaffSearchTerm(e.target.value)}
                            className="search-input"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '16px',
                              marginBottom: '8px',
                              flexShrink: 0
                            }}
                          />
                          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            {displayedStaff.map((s) => {
                              const isSelected = assignFormData.assignedStaffIds.includes(s._id || s.id || '');
                              return (
                                <label
                                  key={s._id || s.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    lineHeight: '1.5'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setAssignFormData({
                                          ...assignFormData,
                                          assignedStaffIds: [...assignFormData.assignedStaffIds, s._id || s.id || ''],
                                        });
                                      } else {
                                        setAssignFormData({
                                          ...assignFormData,
                                          assignedStaffIds: assignFormData.assignedStaffIds.filter((id) => id !== (s._id || s.id)),
                                        });
                                      }
                                    }}
                                    style={{
                                      marginRight: '10px',
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px',
                                      flexShrink: 0
                                    }}
                                  />
                                  <div
                                    style={{
                                      flex: 1,
                                      userSelect: 'none',
                                      transition: 'color 0.3s'
                                    }}
                                  >
                                    <div style={{ fontWeight: isSelected ? '600' : 'normal' }}>
                                      {s.metadata?.name || s.name}
                                    </div>
                                    {s.spec?.phone && (
                                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                                        {s.spec.phone}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                            {hasMore && (
                              <button
                                type="button"
                                onClick={() => setDisplayedStaffLimit(prev => prev + 50)}
                                className="btn-cancel"
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  marginTop: '8px',
                                  fontSize: '16px'
                                }}
                              >
                                Load More ({filteredStaff.length - displayedStaffLimit} remaining)
                              </button>
                            )}
                            {filteredStaff.length === 0 && (
                              <div
                                className="empty-text"
                                style={{
                                  padding: '20px',
                                  textAlign: 'center',
                                  fontStyle: 'italic'
                                }}
                              >
                                {debouncedStaffSearchTerm ? (
                                  <>No staff found matching "{debouncedStaffSearchTerm}"</>
                                ) : (
                                  <>No staff available</>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseAssignModal} disabled={assignSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={assignSubmitting || !assignFormData.ambulanceId || !assignFormData.pilotId}
                  style={{
                    opacity: (assignSubmitting || !assignFormData.ambulanceId || !assignFormData.pilotId) ? 0.5 : 1,
                    cursor: (assignSubmitting || !assignFormData.ambulanceId || !assignFormData.pilotId) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {assignSubmitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      Assigning...
                    </>
                  ) : (
                    'Assign'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Details Drawer */}
      {showDetailsDrawer && selectedBooking && (
        <div className="drawer-overlay" onClick={() => setShowDetailsDrawer(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', minWidth: '670px' }}>
            <div
              className="drawer-header"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}
            >
              <h3 className="drawer-title">Booking Details</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ position: 'relative' }} ref={detailsDownloadMenuRef}>
                  <button
                    onClick={() => !detailsDownloading && setShowDetailsDownloadMenu(!showDetailsDownloadMenu)}
                    disabled={detailsDownloading}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: detailsDownloading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      opacity: detailsDownloading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {detailsDownloading ? 'Generating...' : 'Download'}
                  </button>
                  {showDetailsDownloadMenu && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '5px',
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        border: isDark ? '1px solid #334155' : '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        minWidth: '150px',
                      }}
                    >
                      <button
                        onClick={() => handleDownloadBookingDetails('pdf')}
                        disabled={detailsDownloading}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: isDark ? '#ffffff' : '#333',
                          border: 'none',
                          borderRadius: '6px 6px 0 0',
                          cursor: detailsDownloading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          opacity: detailsDownloading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!detailsDownloading) {
                            e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Download as PDF
                      </button>
                      <button
                        onClick={() => handleDownloadBookingDetails('excel')}
                        disabled={detailsDownloading}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: isDark ? '#ffffff' : '#333',
                          border: 'none',
                          borderTop: isDark ? '1px solid #334155' : '1px solid #ddd',
                          borderRadius: '0 0 6px 6px',
                          cursor: detailsDownloading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          opacity: detailsDownloading ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!detailsDownloading) {
                            e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Download as Excel
                      </button>
                    </div>
                  )}
                </div>
                <button className="drawer-close" onClick={() => setShowDetailsDrawer(false)}>
                  ×
                </button>
              </div>
            </div>

            <div className="drawer-body" style={{ padding: '20px' }}>
              {detailsDownloadError && (
                <div style={{ marginBottom: '10px', color: '#f44336', fontSize: '14px' }}>
                  {detailsDownloadError}
                </div>
              )}
              {(() => {
                const booking = selectedBooking;
                // Use populated data from booking API instead of separate lookups

                // Theme-aware colors
                const cardBg = isDark ? '#1e293b' : '#f9f9f9';
                const cardBorder = isDark ? '#334155' : '#e0e0e0';
                const headingBorder = isDark ? '#334155' : '#e0e0e0';
                const labelColor = isDark ? '#94a3b8' : '#666';
                const textColor = isDark ? '#f1f5f9' : '#0f172a';

                const getStatusColor = (status: string) => {
                  switch (status) {
                    case 'pending':
                      return '#ff9800';
                    case 'assigned':
                      return '#2196f3';
                    case 'in_transit':
                      return '#9c27b0';
                    case 'patient_picked':
                      return '#00bcd4';
                    case 'patient_dropped':
                      return '#795548';
                    case 'completed':
                      return '#4caf50';
                    case 'cancelled':
                      return '#fa003f';
                    default:
                      return '#999';
                  }
                };

                const getPriorityColor = (priority: string) => {
                  switch (priority) {
                    case 'critical':
                      return '#f44336';
                    case 'high':
                      return '#ff9800';
                    case 'medium':
                      return '#2196f3';
                    case 'low':
                      return '#4caf50';
                    default:
                      return '#999';
                  }
                };

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Basic Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Basic Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Reference: </span>
                          <span style={{ marginLeft: '16px', fontWeight: '500' }}>{(booking.metadata?.name ?? '').toUpperCase()}</span>
                        </div>
                        <div style={{ fontSize: '16px', color: textColor, display: 'flex', flexWrap: 'nowrap', gap: '16px', alignItems: 'center' }}>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Status: </span>
                            <span style={{ marginLeft: '8px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  background: getStatusColor(booking.spec.status),
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {booking.spec.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Priority: </span>
                            <span style={{ marginLeft: '8px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  background: getPriorityColor(booking.spec.priority),
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {booking.spec.priority.toUpperCase()}
                              </span>
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Booking Amount: </span>
                            <span style={{ marginLeft: '8px', fontWeight: '600', fontSize: '16px' }}>
                              {booking.spec.bookingAmount ? `₹${Number(booking.spec.bookingAmount).toLocaleString()}` : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Patient Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Patient Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Patient Name:</label>
                          <div style={{ fontSize: '16px', fontWeight: '500', color: textColor }}>
                            {booking.spec.patientInfo?.name || booking.spec.patientId || '—'}
                          </div>
                        </div>
                        {(booking.spec.patientInfo?.number || booking.spec.patientInfo?.age !== undefined || booking.spec.patientInfo?.dateOfBirth || booking.spec.patientInfo?.gender) && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                            {booking.spec.patientInfo?.number && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Phone:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.number}</div>
                              </div>
                            )}
                            {(booking.spec.patientInfo?.age !== undefined || booking.spec.patientInfo?.dateOfBirth) && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Age:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>
                                  {(() => {
                                    const age = booking.spec.patientInfo?.age;
                                    const dob = booking.spec.patientInfo?.dateOfBirth;

                                    // If age is 0 or undefined and DOB exists, calculate age in days
                                    if ((age === 0 || age === undefined) && dob) {
                                      const birthDate = new Date(dob);
                                      const today = new Date();
                                      const diffTime = today.getTime() - birthDate.getTime();
                                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                      return `${diffDays} days`;
                                    }

                                    // If age is defined and >= 1, show the age value with "years" postfix
                                    if (age !== undefined && age >= 1) {
                                      return `${age} years`;
                                    }

                                    // Fallback
                                    return '—';
                                  })()}
                                </div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.dateOfBirth && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Date of Birth:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{formatDateDDMMYYYY(booking.spec.patientInfo.dateOfBirth)}</div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.gender && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Gender:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>
                                  {(() => {
                                    const gender = booking.spec.patientInfo.gender?.toLowerCase();
                                    if (gender === 'male') return 'Male';
                                    if (gender === 'female') return 'Female';
                                    if (gender === 'other' || gender === 'others') return 'Others';
                                    // Fallback: capitalize first letter
                                    return booking.spec.patientInfo.gender.charAt(0).toUpperCase() + booking.spec.patientInfo.gender.slice(1).toLowerCase();
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(booking.spec.patientInfo?.contactPersonName || booking.spec.patientInfo?.contactPersonNumber) && (
                          <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {booking.spec.patientInfo?.contactPersonName && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Contact Person Name:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.contactPersonName}</div>
                              </div>
                            )}
                            {booking.spec.patientInfo?.contactPersonNumber && (
                              <div>
                                <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Contact Person Number:</label>
                                <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.patientInfo.contactPersonNumber}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {booking.spec.medicalCondition && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Medical Condition:</label>
                            <div style={{ fontSize: '16px', color: textColor }}>{booking.spec.medicalCondition}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Assignment Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Assignment Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Booked By:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>{getBookedByName(booking.spec.bookedBy, booking.spec.bookedByDisplay)}</div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Ambulance:</label>
                          <div style={{ fontSize: '16px', color: textColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                            {booking.spec.ambulanceInfo
                              ? (() => {
                                const name = booking.spec.ambulanceInfo.name;
                                const licensePlate = booking.spec.ambulanceInfo.licensePlate;
                                const type = booking.spec.ambulanceInfo.type;

                                if (!name && !licensePlate && !type) return '—';

                                const nameDisplay = name
                                  ? (licensePlate ? `${name} (${licensePlate})` : name)
                                  : (licensePlate ? `(${licensePlate})` : '—');

                                return (
                                  <>
                                    <div style={{ flex: 1 }}>{nameDisplay}</div>
                                    {type && (
                                      <span
                                        style={{
                                          fontSize: '14px',
                                          padding: '3px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: 'transparent',
                                          color: labelColor,
                                          border: `1.5px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                                          fontWeight: 500,
                                          display: 'inline-block',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {type}
                                      </span>
                                    )}
                                  </>
                                );
                              })()
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Pilot/Driver:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.pilotInfo?.name
                              ? (booking.spec.pilotInfo?.number
                                ? `${booking.spec.pilotInfo.name} (${booking.spec.pilotInfo.number})`
                                : booking.spec.pilotInfo.name)
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Assigned Staff:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.assignedStaffInfo && booking.spec.assignedStaffInfo.length > 0 ? (
                              booking.spec.assignedStaffInfo.map((s, idx) => (
                                <div key={idx} style={{ marginBottom: '4px' }}>
                                  {s.name
                                    ? (s.number
                                      ? `${s.name} (${s.number})`
                                      : s.name)
                                    : '—'}
                                </div>
                              ))
                            ) : (
                              '—'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Location Information</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Pickup Location:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.pickupLocation?.address || (
                              booking.spec.pickupLocation
                                ? `${booking.spec.pickupLocation.latitude.toFixed(6)}, ${booking.spec.pickupLocation.longitude.toFixed(6)}`
                                : '—'
                            )}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '17px', color: labelColor, display: 'block', marginBottom: '4px', fontStyle: 'italic', fontWeight: 'bold' }}>Dropoff Location:</label>
                          <div style={{ fontSize: '16px', color: textColor }}>
                            {booking.spec.dropoffLocation?.address || (
                              booking.spec.dropoffLocation
                                ? `${booking.spec.dropoffLocation.latitude.toFixed(6)}, ${booking.spec.dropoffLocation.longitude.toFixed(6)}`
                                : '—'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timings */}
                    <div>
                      <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Timings</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}` }}>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Booking Time: </span>
                          <span style={{ marginLeft: '16px' }}>
                            {booking.spec.timings?.bookingTime
                              ? formatDateTimeDDMMYYYY(booking.spec.timings.bookingTime)
                              : booking.metadata.createdAt
                                ? formatDateTimeDDMMYYYY(booking.metadata.createdAt)
                                : '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: '16px', color: textColor }}>
                          <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Last Update: </span>
                          <span style={{ marginLeft: '16px' }}>
                            {(() => {
                              const lastStatusUpdate = getLastStatusUpdateTime(booking);
                              return lastStatusUpdate ? formatDateTimeDDMMYYYY(lastStatusUpdate) : (booking.metadata.updatedAt ? formatDateTimeDDMMYYYY(booking.metadata.updatedAt) : '—');
                            })()}
                          </span>
                        </div>
                        {booking.spec.timings?.assignedTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Assigned Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.assignedTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.inTransitTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Trip Start: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.inTransitTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.scheduledPickupTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Scheduled Pickup: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.scheduledPickupTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.estimatedArrivalTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Estimated Arrival: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.estimatedArrivalTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.actualArrivalTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Actual Arrival: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.actualArrivalTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.completionTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Completion Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.completionTime)}
                            </span>
                          </div>
                        )}
                        {booking.spec.timings?.cancelledTime && (
                          <div style={{ fontSize: '16px', color: textColor }}>
                            <span style={{ fontSize: '17px', color: labelColor, fontStyle: 'italic', fontWeight: 'bold' }}>Cancelled Time: </span>
                            <span style={{ marginLeft: '16px' }}>
                              {formatDateTimeDDMMYYYY(booking.spec.timings.cancelledTime)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Admin cancellation (when booking cancelled by tenant admin) */}
                    {booking.spec.status === 'cancelled' && booking.spec.cancellationReason && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Cancellation Reason (Admin)</h4>
                        <div style={{ padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, fontSize: '16px', color: textColor }}>
                          {booking.spec.cancellationReason}
                        </div>
                      </div>
                    )}

                    {/* Pilot Assignment History */}
                    {booking.spec.pilotHistory && booking.spec.pilotHistory.length > 0 && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Pilot Assignment History</h4>
                        <div style={{ padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, maxHeight: '300px', overflowY: 'auto' }}>
                          {booking.spec.pilotHistory.map((history, index) => {
                            const getStatusColor = (status: string) => {
                              switch (status) {
                                case 'assigned': return '#2196f3';
                                case 'in_transit': return '#9c27b0';
                                case 'patient_picked': return '#ff9800';
                                case 'patient_dropped': return '#795548';
                                case 'completed': return '#4caf50';
                                case 'cancelled': return '#fa003f';
                                default: return '#666';
                              }
                            };

                            const formatTime = (timestamp?: number) => {
                              if (!timestamp) return '—';
                              return formatDateTimeDDMMYYYY(timestamp);
                            };

                            return (
                              <div
                                key={index}
                                style={{
                                  padding: '12px',
                                  marginBottom: index < booking.spec.pilotHistory!.length - 1 ? '12px' : '0',
                                  borderBottom: index < booking.spec.pilotHistory!.length - 1 ? `1px solid ${cardBorder}` : 'none',
                                  backgroundColor: index === booking.spec.pilotHistory!.length - 1 ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)') : 'transparent',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <div style={{ fontWeight: '600', fontSize: '17px', color: textColor }}>{history.name}</div>
                                  <span
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      color: 'white',
                                      background: getStatusColor(history.status),
                                    }}
                                  >
                                    {history.status.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                                {history.phone && (
                                  <div style={{ fontSize: '15px', color: labelColor, marginBottom: '6px' }}>
                                    Phone: {history.phone}
                                  </div>
                                )}
                                <div style={{ fontSize: '14px', color: labelColor, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  {history.assignedTime && <div>Assigned: {formatTime(history.assignedTime)}</div>}
                                  {history.inTransitTime && <div>In Transit: {formatTime(history.inTransitTime)}</div>}
                                  {(history.patientPickedTime ?? history.arrivedTime) && <div>Patient Picked: {formatTime(history.patientPickedTime ?? history.arrivedTime)}</div>}
                                  {(history as { patientDroppedTime?: number }).patientDroppedTime && <div>Patient Dropped: {formatTime((history as { patientDroppedTime?: number }).patientDroppedTime)}</div>}
                                  {history.completionTime && <div>Completed: {formatTime(history.completionTime)}</div>}
                                  {history.cancelledTime && <div>Cancelled: {formatTime(history.cancelledTime)}</div>}
                                  {history.status === 'cancelled' && history.cancellationReason && (
                                    <div style={{ marginTop: '6px', fontStyle: 'italic' }}>Reason: {history.cancellationReason}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {booking.spec.notes && (
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600', color: textColor }}>Notes</h4>
                        <div style={{ fontSize: '16px', padding: '16px', backgroundColor: cardBg, borderRadius: '8px', border: `1px solid ${cardBorder}`, whiteSpace: 'pre-wrap', color: textColor }}>
                          {booking.spec.notes}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Booking"
        message={`Are you sure you want to delete the booking "${bookingToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setBookingToDelete(null);
        }}
      />

      <CancelBookingModal
        isOpen={showCancelBookingConfirm}
        onConfirm={confirmCancelBooking}
        onCancel={() => {
          setShowCancelBookingConfirm(false);
          setBookingToCancel(null);
        }}
      />

      <CancelBookingModal
        isOpen={showCancelPilotAssignmentConfirm}
        onConfirm={confirmCancelPilotAssignment}
        onCancel={() => {
          setShowCancelPilotAssignmentConfirm(false);
          setPilotAssignmentToCancel(null);
        }}
      />

    </div>
  );
};

export default AllBookingsTab;
